import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type {
  GetRecentActivitiesApiResponse,
  RecentActivityListItemApiResponse,
} from "@/types/api/recent-activities";
import type {
  CollectionActivity,
  CollectionCommunication,
  CaseNote,
} from "@/types/collections";

export interface DisputeActivityData {
  activities: CollectionActivity[];
  communications: CollectionCommunication[];
  notes: CaseNote[];
}

const COMMUNICATION_TYPES = new Set(["email", "phone", "sms", "communication"]);

function mapApiToDisputeActivityData(
  items: RecentActivityListItemApiResponse[],
  disputeId: string
): DisputeActivityData {
  const activities: CollectionActivity[] = [];
  const communications: CollectionCommunication[] = [];
  const notes: CaseNote[] = [];

  for (const item of items) {
    const createdAt = new Date(item.createdAt);

    if (item.activityType === "note") {
      notes.push({
        id: String(item.id),
        caseId: disputeId,
        content: item.description,
        createdBy: item.actorName,
        createdAt,
      });
    } else if (COMMUNICATION_TYPES.has(item.activityType)) {
      let direction: "inbound" | "outbound" = "outbound";
      let subject: string | undefined;
      if (item.metadataJson) {
        try {
          const metadata = JSON.parse(item.metadataJson) as Record<string, unknown>;
          if (metadata.direction === "inbound" || metadata.direction === "outbound") {
            direction = metadata.direction;
          }
          if (typeof metadata.subject === "string") {
            subject = metadata.subject;
          }
        } catch {
          // ignore malformed metadata
        }
      }
      // Resolve the communication sub-type: if activityType is the generic "communication",
      // infer the specific type from the title; otherwise use the activityType directly.
      let commType: "email" | "phone" | "sms" = "phone";
      if (item.activityType === "email" || item.activityType === "phone" || item.activityType === "sms") {
        commType = item.activityType;
      } else if (item.title?.toLowerCase().includes("email")) {
        commType = "email";
      }
      communications.push({
        id: String(item.id),
        caseId: disputeId,
        type: commType,
        direction,
        subject,
        content: item.description,
        sentAt: createdAt,
        sentBy: item.actorName,
        status: "sent",
      });
    } else {
      const activityType = mapActivityType(item.activityType);
      let outcome: string | undefined;
      if (item.metadataJson) {
        try {
          const metadata = JSON.parse(item.metadataJson) as Record<string, unknown>;
          if (typeof metadata.outcome === "string") {
            outcome = metadata.outcome;
          }
        } catch {
          // ignore malformed metadata
        }
      }
      activities.push({
        id: String(item.id),
        caseId: disputeId,
        type: activityType,
        description: item.description,
        outcome,
        createdBy: item.actorName,
        createdAt,
      });
    }
  }

  return { activities, communications, notes };
}

function mapActivityType(
  activityType: string
): CollectionActivity["type"] {
  const typeMap: Record<string, CollectionActivity["type"]> = {
    status_change: "status_change",
    payment: "payment",
    promise: "promise",
    workflow_progress: "workflow_progress",
    document_upload: "document_upload",
  };
  return typeMap[activityType] ?? "status_change";
}

function buildMockDisputeActivityData(): DisputeActivityData {
  return {
    activities: [
      {
        id: "mock-act-1",
        caseId: "MOCK",
        type: "status_change",
        description: "Status changed from 'New' to 'In Progress'",
        createdBy: "John Smith",
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      },
      {
        id: "mock-act-2",
        caseId: "MOCK",
        type: "status_change",
        description: "Priority changed from 'Medium' to 'High'",
        createdBy: "System",
        createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      },
    ],
    communications: [
      {
        id: "mock-comm-1",
        caseId: "MOCK",
        type: "email",
        direction: "outbound",
        subject: "Re: Dispute update",
        content: "Thank you for bringing this to our attention. We are currently reviewing...",
        sentBy: "support@company.com",
        sentAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        status: "sent",
      },
      {
        id: "mock-comm-2",
        caseId: "MOCK",
        type: "email",
        direction: "inbound",
        subject: "Dispute regarding invoice",
        content: "I would like to dispute the charges on this invoice...",
        sentBy: "customer@example.com",
        sentAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        status: "delivered",
      },
    ],
    notes: [
      {
        id: "mock-note-1",
        caseId: "MOCK",
        content: "Contacted customer to verify service dates. Awaiting response.",
        createdBy: "John Smith",
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      },
      {
        id: "mock-note-2",
        caseId: "MOCK",
        content: "Dispute automatically categorized with 85% confidence.",
        createdBy: "AI Agent",
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      },
    ],
  };
}

export function useDisputeRecentActivities(disputeId: string | undefined) {
  const { apiGet } = useApi();
  const mockData = buildMockDisputeActivityData();

  return useModeAwareQuery<DisputeActivityData, Error>(
    ["dispute-recent-activities", disputeId],
    async ({ signal }) => {
      if (!disputeId) {
        return { activities: [], communications: [], notes: [] };
      }
      try {
        const response = await apiGet<GetRecentActivitiesApiResponse>(
          `/api/v1/recent-activities/disputes/${disputeId}`,
          { page: 1, pageSize: 50 },
          signal
        );
        return mapApiToDisputeActivityData(response.data, disputeId);
      } catch (error) {
        console.error("Error fetching dispute recent activities:", error);
        return { activities: [], communications: [], notes: [] };
      }
    },
    mockData,
    {
      enabled: !!disputeId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
